import { ref, watch, readonly, computed } from 'vue'
import type { LocationQuery, LocationQueryValue, RouteParams } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { captureException } from '@sentry/vue'
import { z } from 'zod'
import type { RangeType, KarteUserFilteringRule } from '@/types/app'
import { PREFECTURE_CODES } from '@/features/prefecture/jisx0402'
import { emptyKarteUserFilteringRule } from '@/pages/KarteListPage/hooks/useKarteUserListFilterForm'
import { Channel } from '@/types/schema.generated'
import { convertKarteUserFilteringRuleToLogData } from '@/logger/dataConvert'
import { useLogger } from '@/logger/logger'

export const DEFAULT_FORM_STATE: KarteUserFilteringRuleForm = {
  annualIncome: {
    min: null,
    max: null,
  },
  partnerAnnualIncome: {
    min: null,
    max: null,
  },
  saving: {
    min: null,
    max: null,
  },
  investment: {
    min: null,
    max: null,
  },
  totalAssets: {
    min: null,
    max: null,
  },
  monthlyInsuranceFee: {
    min: null,
    max: null,
  },
  loanBalance: {
    min: null,
    max: null,
  },
  generation: [],
  prefecture: null,
  city: null,
  sex: 'notSelected',
  partnerExistence: 'notSelected',
  childrenExistence: 'notSelected',
  hasAnswered: 'notSelected',
  hasConcern: 'existConcern',
  channel: null,
  jobIds: [],
}

const hasConcern = z.union([
  z.literal('notSelected'),
  z.literal('existConcern'),
  z.literal('noConcern'),
])

export type HasConcern = z.infer<typeof hasConcern>

const childrenExistence = z.union([
  z.literal('notSelected'),
  z.literal('existChildren'),
  z.literal('noChildren'),
])

export type ChildrenExistence = z.infer<typeof childrenExistence>

const partnerExistence = z.union([
  z.literal('notSelected'),
  z.literal('existPartner'),
  z.literal('noPartner'),
])

export type PartnerExistence = z.infer<typeof partnerExistence>

const generation = z.union([
  z.literal(0),
  z.literal(30),
  z.literal(40),
  z.literal(50),
  z.literal(60),
])

export type Generation = z.infer<typeof generation>

const jobIds = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
  z.literal(10),
  z.literal(11),
  z.literal(12),
  z.literal(13),
])

export type JobIds = z.infer<typeof jobIds>

const sex = z.union([
  z.literal('notSelected'),
  z.literal('male'),
  z.literal('female'),
])

export type Sex = z.infer<typeof sex>

const hasAnswered = z.union([
  z.literal('notSelected'),
  z.literal('answered'),
  z.literal('notAnswered'),
])

export type HasAnswered = z.infer<typeof hasAnswered>

const formChannelSchema = z.string().nullable().default(null)

export type FormChannel = z.infer<typeof formChannelSchema>

const prefecture = z
  .union([
    ...(PREFECTURE_CODES.map(({ id }) => z.literal(id)) as [
      z.ZodLiteral<string>,
      z.ZodLiteral<string>,
      ...z.ZodLiteral<string>[],
    ]),
  ])
  .nullable()
  .default(null)

export type Prefecture = z.infer<typeof prefecture>

const city = z.string().nullable().default(null)

const millionUnit = z
  .union([
    z.literal(1_000_000),
    z.literal(2_000_000),
    z.literal(3_000_000),
    z.literal(4_000_000),
    z.literal(5_000_000),
    z.literal(6_000_000),
    z.literal(7_000_000),
    z.literal(8_000_000),
    z.literal(9_000_000),
    z.literal(10_000_000),
    z.literal(20_000_000),
    z.literal(30_000_000),
    z.literal(40_000_000),
    z.literal(50_000_000),
    z.literal(60_000_000),
    z.literal(70_000_000),
    z.literal(80_000_000),
    z.literal(90_000_000),
    z.literal(100_000_000),
  ])
  .nullable()
  .default(null)

export type MillionUnit = z.infer<typeof millionUnit>

const tenThousandUnit = z
  .union([
    z.literal(10_000),
    z.literal(20_000),
    z.literal(30_000),
    z.literal(40_000),
    z.literal(50_000),
    z.literal(60_000),
    z.literal(70_000),
    z.literal(80_000),
    z.literal(90_000),
    z.literal(100_000),
  ])
  .nullable()
  .default(null)

export type TenThousandUnit = z.infer<typeof tenThousandUnit>

const annualIncome = z.object({
  min: millionUnit,
  max: millionUnit,
})

const partnerAnnualIncome = z.object({
  min: millionUnit,
  max: millionUnit,
})

const saving = z.object({
  min: millionUnit,
  max: millionUnit,
})

const investment = z.object({
  min: millionUnit,
  max: millionUnit,
})

// 後からフィルタ条件を追加したので、totalAssetsだけデフォ値を設定してる。
// 後で他のフィルタ条件もデフォ値を設定する。
const totalAssets = z
  .object({
    min: millionUnit,
    max: millionUnit,
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  .default(DEFAULT_FORM_STATE.totalAssets as any) // TODO: 型の出自をリファクタで同一にしてanyはがす

const monthlyInsuranceFee = z.object({
  min: tenThousandUnit,
  max: tenThousandUnit,
})

const loanBalance = z.object({
  min: millionUnit,
  max: millionUnit,
})

const formSchema = z.object({
  annualIncome,
  partnerAnnualIncome,
  saving,
  investment,
  totalAssets,
  monthlyInsuranceFee,
  loanBalance,
  generation: z.array(generation),
  prefecture: prefecture,
  city,
  sex,
  partnerExistence,
  childrenExistence,
  hasAnswered,
  hasConcern,
  channel: formChannelSchema,
  jobIds: z.array(jobIds),
})

export type KarteUserFilteringRuleForm = {
  annualIncome: RangeType
  partnerAnnualIncome: RangeType
  saving: RangeType
  investment: RangeType
  totalAssets: RangeType
  monthlyInsuranceFee: RangeType
  loanBalance: RangeType
  generation: Generation[]
  prefecture: Prefecture | null
  city: string | null
  sex: Sex
  partnerExistence: PartnerExistence
  childrenExistence: ChildrenExistence
  hasAnswered: HasAnswered
  hasConcern: HasConcern
  channel: string | null
  jobIds: number[]
}

type DeepStringify<T> = {
  [P in keyof T]-?: T[P] extends { [property: string]: unknown }
    ? DeepStringify<T[P]>
    : NonNullable<string>
}

// 検索条件が追加されたときに、対応していないと型エラーになるように型をつくる
type QueryParamKey = DeepStringify<KarteUserFilteringRuleForm>

export type FavoriteKarteUserFilteringRule = {
  label: string
  rule: KarteUserFilteringRuleForm
}

// クエリパラメータに検索条件を持っているかどうかの判定の際、検索条件以外のパラメータが付与されたときに判別できるように prefix をつける
const qsPrefix = 'rule'

const queryParamKey: QueryParamKey = {
  annualIncome: {
    min: `${qsPrefix}.annualIncome.min`,
    max: `${qsPrefix}.annualIncome.max`,
  },
  partnerAnnualIncome: {
    min: `${qsPrefix}.partnerAnnualIncome.min`,
    max: `${qsPrefix}.partnerAnnualIncome.max`,
  },
  saving: {
    min: `${qsPrefix}.saving.min`,
    max: `${qsPrefix}.saving.max`,
  },
  investment: {
    min: `${qsPrefix}.investment.min`,
    max: `${qsPrefix}.investment.max`,
  },
  totalAssets: {
    min: `${qsPrefix}.totalAssets.min`,
    max: `${qsPrefix}.totalAssets.max`,
  },
  monthlyInsuranceFee: {
    min: `${qsPrefix}.monthlyInsuranceFee.min`,
    max: `${qsPrefix}.monthlyInsuranceFee.max`,
  },
  loanBalance: {
    min: `${qsPrefix}.loanBalance.min`,
    max: `${qsPrefix}.loanBalance.max`,
  },
  generation: `${qsPrefix}.generation`,
  prefecture: `${qsPrefix}.prefecture`,
  city: `${qsPrefix}.city`,
  sex: `${qsPrefix}.sex`,
  partnerExistence: `${qsPrefix}.partnerExistence`,
  childrenExistence: `${qsPrefix}.childrenExistence`,
  hasAnswered: `${qsPrefix}.hasAnswered`,
  hasConcern: `${qsPrefix}.hasConcern`,
  channel: `${qsPrefix}.channel`,
  jobIds: `${qsPrefix}.jobIds`,
}

// 前回の検索条件の保存(SPA遷移で揮発しないようにする)
const FILTERING_RULE_KEY = 'FILTERING_RULE_KEY'
// お気に入りの検索条件リストの保存
const FAVORITE_FILTERING_RULE_KEY = 'FAVORITE_FILTERING_RULE_KEY'

export function useKarteUserFilteringRule() {
  const route = useRoute()
  const logger = useLogger()

  const hasRuleOnQueryParameter = computed(() =>
    Object.keys(route.query).some(key => key.startsWith(qsPrefix))
  )

  const initialRule = (() => {
    if (hasRuleOnQueryParameter.value) {
      // クエリパラメータで条件指定がある場合は、localStorage から復元しない
      // 挙動が意図的ではないとしてバグ報告が来た
      // https://400f-jp.slack.com/archives/C014WA21T4J/p1665734790709019
      return (
        buildInitialStateFromQueryParameter(route.query) ?? DEFAULT_FORM_STATE
      )
    }
    return buildInitialStateFromLocalStorage() ?? DEFAULT_FORM_STATE
  })()

  const ruleRef = ref<KarteUserFilteringRuleForm>(initialRule)
  const queryString = computed(() => route.fullPath.split('?')[1] ?? '')
  watch(
    queryString,
    value => {
      if (value === '') {
        return
      }
      ruleRef.value = hasRuleOnQueryParameter.value
        ? (buildInitialStateFromQueryParameter(route.query) ??
          DEFAULT_FORM_STATE)
        : DEFAULT_FORM_STATE
    },
    { immediate: true }
  )

  const initialFavoriteFilteringRules =
    ((): FavoriteKarteUserFilteringRule[] => {
      const saved = localStorage.getItem(FAVORITE_FILTERING_RULE_KEY)
      try {
        if (!saved) {
          return []
        }

        const rules = (
          JSON.parse(saved) as { label?: string; rule?: unknown }[]
        ).filter((favorite): favorite is { label: string; rule: unknown } => {
          return favorite.label != undefined && favorite.rule != undefined
        })

        const parsedRules = rules
          .map(({ label, rule }) => {
            const result = formSchema.safeParse(rule)

            if (!result.success) {
              return null
            }

            return {
              label,
              rule: result.data as KarteUserFilteringRuleForm, // TODO: リファクタして型の出自を1つにしたらtype assertion消す
            }
          })
          .filter(
            (rule): rule is FavoriteKarteUserFilteringRule => rule != null
          )

        localStorage.setItem(
          FAVORITE_FILTERING_RULE_KEY,
          JSON.stringify(parsedRules)
        )

        return parsedRules
      } catch (error) {
        captureException(error)
        return []
      }
    })()

  const favoriteFilteringRulesRef = ref<FavoriteKarteUserFilteringRule[]>(
    initialFavoriteFilteringRules
  )

  const selectedFavoriteFilteringRuleIndex = ref<Maybe<number>>()

  const loadFavoriteFilteringRule = () => {
    if (selectedFavoriteFilteringRule.value) {
      ruleRef.value = selectedFavoriteFilteringRule.value.rule
    }
  }

  const saveFavoriteFilteringRule = (label: string): boolean => {
    const rule = cloneRule(ruleRef.value)
    const newFavoriteFilteringRule = [
      ...favoriteFilteringRulesRef.value,
      { label, rule },
    ].filter(value => value?.label != null && value?.rule != null)

    // localStorageへの保存
    try {
      window.localStorage.setItem(
        FAVORITE_FILTERING_RULE_KEY,
        JSON.stringify(newFavoriteFilteringRule)
      )
    } catch (e) {
      captureException(e)
      return false
    }

    // ltl-peopleログ
    logger.ltlPeople.event.saveKarteUserFilteringRule(logger.buildMeta(), {
      data: {
        filteringRuleName: label,
        rule: convertKarteUserFilteringRuleToLogData(
          filteringRuleFormToFilteringRule(rule)
        ),
      },
    })

    favoriteFilteringRulesRef.value = newFavoriteFilteringRule
    selectedFavoriteFilteringRuleIndex.value =
      favoriteFilteringRulesRef.value.length - 1
    return true
  }

  const updateFavoriteFilteringRule = (updatedLabel: string): boolean => {
    if (selectedFavoriteFilteringRuleIndex.value === undefined) {
      return false
    }

    const rule =
      favoriteFilteringRulesRef.value[selectedFavoriteFilteringRuleIndex.value]
    if (!rule) {
      return false
    }

    const beforeFavoriteFilteringRule = {
      label: rule.label,
      rule: cloneRule(rule.rule),
    }

    const updatedFavoriteFilteringRules = [
      ...favoriteFilteringRulesRef.value.slice(
        0,
        selectedFavoriteFilteringRuleIndex.value
      ),
      { label: updatedLabel, rule: cloneRule(ruleRef.value) },
      ...favoriteFilteringRulesRef.value.slice(
        selectedFavoriteFilteringRuleIndex.value + 1
      ),
    ]
    // localStorageへの保存
    try {
      window.localStorage.setItem(
        FAVORITE_FILTERING_RULE_KEY,
        JSON.stringify(updatedFavoriteFilteringRules)
      )
    } catch (e) {
      captureException(e)
      return false
    }

    // ltl-peopleログ
    logger.ltlPeople.event.updateKarteUserFilteringRule(logger.buildMeta(), {
      data: {
        before: {
          filteringRuleName: beforeFavoriteFilteringRule.label,
          rule: convertKarteUserFilteringRuleToLogData(
            filteringRuleFormToFilteringRule(beforeFavoriteFilteringRule.rule)
          ),
        },
        after: {
          filteringRuleName: updatedLabel,
          rule: convertKarteUserFilteringRuleToLogData(
            filteringRuleFormToFilteringRule(ruleRef.value)
          ),
        },
      },
    })

    favoriteFilteringRulesRef.value = updatedFavoriteFilteringRules
    return true
  }

  const removeFavoriteFilteringRule = (): boolean => {
    const index = selectedFavoriteFilteringRuleIndex.value

    if (index === undefined) {
      return false
    }

    const target = favoriteFilteringRulesRef.value[index]
    const logParams = {
      filteringRuleName: target.label,
      rule: filteringRuleFormToFilteringRule(target.rule),
    }

    const newRules = [
      ...favoriteFilteringRulesRef.value.slice(0, index),
      ...favoriteFilteringRulesRef.value.slice(index + 1),
    ]
    // localStorageへの保存
    try {
      window.localStorage.setItem(
        FAVORITE_FILTERING_RULE_KEY,
        JSON.stringify(newRules)
      )
    } catch (e) {
      captureException(e)
      return false
    }

    // ltl-peopleログ
    logger.ltlPeople.event.removeKarteUserFilteringRule(logger.buildMeta(), {
      data: {
        filteringRuleName: logParams.filteringRuleName,
        rule: convertKarteUserFilteringRuleToLogData(logParams.rule),
      },
    })

    favoriteFilteringRulesRef.value = newRules
    selectedFavoriteFilteringRuleIndex.value = undefined
    return true
  }

  const router = useRouter()
  watch(
    ruleRef,
    async newRule => {
      try {
        window.localStorage.setItem(FILTERING_RULE_KEY, JSON.stringify(newRule))
        const queryForRule = toQueryParams(newRule)

        // クエリパラメータの引き継ぎに再セットについて、
        // フィルタ条件に関するものはクリアして、queryForRule の内容のみをセットする
        // たとえばトラッキング系など、フィルタ条件以外のクエリパラメータがある場合は、引き継いでほしいので
        const exceptedKarteFilteringRuleQuery = Object.entries(
          route.query
        ).reduce((acc, [key, value]) => {
          const isKarteFilteringParam = key.startsWith('rule.')
          return !isKarteFilteringParam ? { ...acc, [key]: value } : acc
        }, {})

        const query = {
          ...exceptedKarteFilteringRuleQuery,
          ...queryForRule,
        }

        // 検索条件の更新が history に積み上がる必要はないので push ではなく replace を使う
        await router.replace({ query })
      } catch (e) {
        captureException(e)
      }
    },
    {
      deep: true,
      // あらかじめ LocalStorage に保存されていた文字列が不正だった場合、initialState が書き込まれるようにする
      immediate: true,
    }
  )

  const selectedFavoriteFilteringRule = computed(() =>
    selectedFavoriteFilteringRuleIndex.value != null
      ? favoriteFilteringRulesRef.value[
          selectedFavoriteFilteringRuleIndex.value
        ]
      : undefined
  )

  return {
    ruleRef,
    favoriteFilteringRulesRef: readonly(favoriteFilteringRulesRef),
    selectedFavoriteFilteringRuleIndex,
    selectedFavoriteFilteringRule,
    loadFavoriteFilteringRule,
    saveFavoriteFilteringRule,
    updateFavoriteFilteringRule,
    removeFavoriteFilteringRule,
  }
}

function buildInitialStateFromQueryParameter(
  queryObj: LocationQuery
): Maybe<KarteUserFilteringRuleForm> {
  // prettier-ignore
  const parsedValue = {
    annualIncome: {
      min: safeNumberParse(queryObj[queryParamKey['annualIncome'].min]),
      max: safeNumberParse(queryObj[queryParamKey['annualIncome'].max]),
    },
    partnerAnnualIncome: {
      min: safeNumberParse(queryObj[queryParamKey['partnerAnnualIncome'].min]),
      max: safeNumberParse(queryObj[queryParamKey['partnerAnnualIncome'].max]),
    },
    saving: {
      min: safeNumberParse(queryObj[queryParamKey['saving'].min]),
      max: safeNumberParse(queryObj[queryParamKey['saving'].max]),
    },
    investment: {
      min: safeNumberParse(queryObj[queryParamKey['investment'].min]),
      max: safeNumberParse(queryObj[queryParamKey['investment'].max]),
    },
    totalAssets: {
      min: safeNumberParse(queryObj[queryParamKey['totalAssets'].min]),
      max: safeNumberParse(queryObj[queryParamKey['totalAssets'].max]),
    },
    monthlyInsuranceFee: {
      min: safeNumberParse(queryObj[queryParamKey['monthlyInsuranceFee'].min]),
      max: safeNumberParse(queryObj[queryParamKey['monthlyInsuranceFee'].max]),
    },
    loanBalance: {
      min: safeNumberParse(queryObj[queryParamKey['loanBalance'].min]),
      max: safeNumberParse(queryObj[queryParamKey['loanBalance'].max]),
    },
    generation: safeNumberArrayParse(queryObj[queryParamKey['generation']]) ?? [],
    prefecture: safeStringParse(queryObj[queryParamKey['prefecture']]),
    city: safeStringParse(queryObj[queryParamKey['city']]),
    sex: safeStringParse(queryObj[queryParamKey['sex']]) ?? 'notSelected',
    partnerExistence: safeStringParse(queryObj[queryParamKey['partnerExistence']]) ??  'notSelected',
    childrenExistence: safeStringParse(queryObj[queryParamKey['childrenExistence']]) ??  'notSelected',
    hasAnswered: safeStringParse(queryObj[queryParamKey['hasAnswered']]) ?? 'notSelected',
    hasConcern: safeStringParse(queryObj[queryParamKey['hasConcern']]) ?? 'notSelected',
    channel: safeChannelValueParse(queryObj[queryParamKey['channel']]),
    jobIds: safeNumberArrayParse(queryObj[queryParamKey['jobIds']]) ?? [],
  }

  try {
    return parseKarteUserFilteringRuleForm(parsedValue)
  } catch (e) {
    captureException(e)
    // 不正な文字列が書き込まれた場合、なにもセットせず、そのまま DEFAULT_FORM_STATE を使う

    return undefined
  }
}

const filterParamObject = (
  value: Record<string, string | string[] | null | undefined>
): Record<string, string | string[]> =>
  Object.entries(value).reduce(
    (acc, [key, value]) => (value != null ? { ...acc, [key]: value } : acc),
    {}
  )

export function toQueryParams(rule: KarteUserFilteringRuleForm): RouteParams {
  // prettier-ignore
  const values = {
    [queryParamKey.annualIncome.min]: rule.annualIncome.min?.toString(),
    [queryParamKey.annualIncome.max]: rule.annualIncome.max?.toString(),
    [queryParamKey.partnerAnnualIncome.min]: rule.partnerAnnualIncome.min?.toString(),
    [queryParamKey.partnerAnnualIncome.max]: rule.partnerAnnualIncome.max?.toString(),
    [queryParamKey.saving.min]: rule.saving.min?.toString(),
    [queryParamKey.saving.max]: rule.saving.max?.toString(),
    [queryParamKey.investment.min]: rule.investment.min?.toString(),
    [queryParamKey.investment.max]: rule.investment.max?.toString(),
    [queryParamKey.totalAssets.min]: rule.totalAssets.min?.toString(),
    [queryParamKey.totalAssets.max]: rule.totalAssets.max?.toString(),
    [queryParamKey.monthlyInsuranceFee.min]: rule.monthlyInsuranceFee.min?.toString(),
    [queryParamKey.monthlyInsuranceFee.max]: rule.monthlyInsuranceFee.max?.toString(),
    [queryParamKey.loanBalance.min]: rule.loanBalance.min?.toString(),
    [queryParamKey.loanBalance.max]: rule.loanBalance.max?.toString(),
    [queryParamKey.generation]: rule.generation.map(v => v.toString()),
    [queryParamKey.prefecture]: rule.prefecture,
    [queryParamKey.city]: rule.city,
    [queryParamKey.sex]: rule.sex,
    [queryParamKey.partnerExistence]: rule.partnerExistence,
    [queryParamKey.childrenExistence]: rule.childrenExistence,
    [queryParamKey.hasAnswered]: rule.hasAnswered,
    [queryParamKey.hasConcern]: rule.hasConcern,
    [queryParamKey.channel]: rule.channel?.toString(),
    [queryParamKey.jobIds]: rule.jobIds.map(v => v.toString()),
  }

  return filterParamObject(values)
}

function safeChannelValueParse(
  val: LocationQueryValue | LocationQueryValue[]
): Channel | undefined {
  if (val == undefined) {
    return undefined
  }

  const strVal = safeStringParse(val)
  if (strVal == undefined) {
    return undefined
  }

  const ch = Object.values(Channel).find(v => v === strVal)
  return ch
}

function safeNumberParse(
  val: LocationQueryValue | LocationQueryValue[]
): number | undefined {
  if (val == undefined) {
    return undefined
  }

  const parsed = Number(val)
  return Number.isNaN(parsed) ? undefined : parsed
}

function safeStringParse(
  val: LocationQueryValue | LocationQueryValue[]
): string | undefined {
  if (val == undefined || Array.isArray(val)) {
    return undefined
  }

  return String(val)
}

function safeNumberArrayParse(
  _val: LocationQueryValue | LocationQueryValue[]
): number[] | undefined {
  const val = Array.isArray(_val) ? _val : [_val]
  return val.map(v => Number(v)).filter(v => !Number.isNaN(v))
}

const parseKarteUserFilteringRuleForm = (
  data: unknown
): Maybe<KarteUserFilteringRuleForm> => {
  const result = formSchema.safeParse(data)

  if (!result.success) {
    return undefined
  }

  return {
    annualIncome: {
      min: result.data.annualIncome.min,
      max: result.data.annualIncome.max,
    },
    partnerAnnualIncome: {
      min: result.data.partnerAnnualIncome.min,
      max: result.data.partnerAnnualIncome.max,
    },
    saving: {
      min: result.data.saving.min,
      max: result.data.saving.max,
    },
    investment: {
      min: result.data.investment.min,
      max: result.data.investment.max,
    },
    totalAssets: {
      min: result.data.totalAssets.min,
      max: result.data.totalAssets.max,
    },
    monthlyInsuranceFee: {
      min: result.data.monthlyInsuranceFee.min,
      max: result.data.monthlyInsuranceFee.max,
    },
    loanBalance: {
      min: result.data.loanBalance.min,
      max: result.data.loanBalance.max,
    },
    generation: result.data.generation,
    prefecture: result.data.prefecture,
    city: result.data.city,
    sex: result.data.sex,
    partnerExistence: result.data.partnerExistence,
    childrenExistence: result.data.childrenExistence,
    hasAnswered: result.data.hasAnswered,
    hasConcern: result.data.hasConcern,
    channel: result.data.channel,
    jobIds: result.data.jobIds,
  }
}

function buildInitialStateFromLocalStorage():
  | KarteUserFilteringRuleForm
  | undefined {
  const rawValue = window.localStorage.getItem(FILTERING_RULE_KEY)
  try {
    if (rawValue != null) {
      const jsonParsed = JSON.parse(rawValue)

      return parseKarteUserFilteringRuleForm(jsonParsed)
    }
  } catch (e) {
    captureException(e)
    // 不正な文字列が書き込まれた場合、なにもセットせず、そのまま DEFAULT_FORM_STATE を使う
  }
  return undefined
}

// KarteUserFilteringRuleForm を KarteUserFilteringRule に変換する関数
// FIXME: ltl-peopleのログをKarteUserFilteringRuleではなくKarteUserFilteringRuleFormに寄せてこの処理を爆破
const filteringRuleFormToFilteringRule = (
  ruleForm: KarteUserFilteringRuleForm
): KarteUserFilteringRule => ({
  annualIncome: {
    min:
      ruleForm.annualIncome.min ?? emptyKarteUserFilteringRule.annualIncome.min,
    max:
      ruleForm.annualIncome.max ?? emptyKarteUserFilteringRule.annualIncome.max,
  },
  partnerAnnualIncome: {
    min:
      ruleForm.partnerAnnualIncome.min ??
      emptyKarteUserFilteringRule.partnerAnnualIncome.min,
    max:
      ruleForm.partnerAnnualIncome.max ??
      emptyKarteUserFilteringRule.partnerAnnualIncome.max,
  },
  saving: {
    min: ruleForm.saving.min ?? emptyKarteUserFilteringRule.saving.min,
    max: ruleForm.saving.max ?? emptyKarteUserFilteringRule.saving.max,
  },
  investment: {
    min: ruleForm.investment.min ?? emptyKarteUserFilteringRule.investment.min,
    max: ruleForm.investment.max ?? emptyKarteUserFilteringRule.investment.max,
  },
  totalAssets: {
    min:
      ruleForm.totalAssets.min ?? emptyKarteUserFilteringRule.totalAssets.min,
    max:
      ruleForm.totalAssets.max ?? emptyKarteUserFilteringRule.totalAssets.max,
  },
  monthlyInsuranceFee: {
    min:
      ruleForm.monthlyInsuranceFee.min ??
      emptyKarteUserFilteringRule.monthlyInsuranceFee.min,
    max:
      ruleForm.monthlyInsuranceFee.max ??
      emptyKarteUserFilteringRule.monthlyInsuranceFee.max,
  },
  loanBalance: {
    min:
      ruleForm.loanBalance.min ?? emptyKarteUserFilteringRule.loanBalance.min,
    max:
      ruleForm.loanBalance.max ?? emptyKarteUserFilteringRule.loanBalance.max,
  },
  generation: ruleForm.generation.map(value => ({
    min: value,
    max: value === 0 ? 29 : value === 60 ? Number.MAX_SAFE_INTEGER : value + 9,
  })),
  prefecture: ruleForm.prefecture,
  city: ruleForm.city,
  sex: ((sex: 'male' | 'female' | 'notSelected') => {
    if (sex === 'male') {
      return 'male'
    }
    if (sex === 'female') {
      return 'female'
    }
    return emptyKarteUserFilteringRule.sex
  })(ruleForm.sex),
  hasPartner: _defineBoolRule(
    'partnerExistence',
    {
      true: 'existPartner',
      false: 'noPartner',
      empty: emptyKarteUserFilteringRule.hasPartner,
    },
    ruleForm
  ),
  hasChild: _defineBoolRule(
    'childrenExistence',
    {
      true: 'existChildren',
      false: 'noChildren',
      empty: emptyKarteUserFilteringRule.hasChild,
    },
    ruleForm
  ),
  hasAnswered: _defineBoolRule(
    'hasAnswered',
    {
      true: 'answered',
      false: 'notAnswered',
      empty: emptyKarteUserFilteringRule.hasAnswered,
    },
    ruleForm
  ),
  hasConcern: _defineBoolRule(
    'hasConcern',
    {
      true: 'existConcern',
      false: 'noConcern',
      empty: emptyKarteUserFilteringRule.hasConcern,
    },
    ruleForm
  ),
  channel: ruleForm.channel,
  jobIds: ruleForm.jobIds,
})

// defineBoolRule を任意のruleFormでえきよう適用できるようにした関数
// FIXME: ltl-peopleのログをKarteUserFilteringRuleではなくKarteUserFilteringRuleFormに寄せてこの処理を爆破
const _defineBoolRule = <K extends keyof KarteUserFilteringRuleForm>(
  ruleKey: K,
  option: { true: string; false: string; empty: boolean | null },
  ruleForm: KarteUserFilteringRuleForm
) => {
  const value = ruleForm[ruleKey]
  if (value === option.true) {
    return true
  }
  if (value === option.false) {
    return false
  }
  return option.empty
}

const cloneRule = (
  rule: KarteUserFilteringRuleForm
): KarteUserFilteringRuleForm => ({
  ...rule,
  annualIncome: {
    ...rule.annualIncome,
  },
  partnerAnnualIncome: {
    ...rule.partnerAnnualIncome,
  },
  saving: {
    ...rule.saving,
  },
  investment: {
    ...rule.investment,
  },
  monthlyInsuranceFee: {
    ...rule.monthlyInsuranceFee,
  },
  generation: [...rule.generation],
})
